import { gapi } from "gapi-script";
import { useGoogleLogin, googleLogout } from "@react-oauth/google";
import { useSyncExternalStore, useCallback, useEffect } from "react";

export const CLIENT_ID =
  "791656083371-20fe92r5aq46otpm37miktc966919gfv.apps.googleusercontent.com";
export const SCOPES =
  "https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/drive.file";

export function initGapiClient() {
  return new Promise((resolve, reject) => {
    try {
      gapi.load("client", async () => {
        try {
          await gapi.client.init({
            clientId: CLIENT_ID,
            scope: SCOPES,
            discoveryDocs: [
              "https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest",
            ],
          });
          resolve(true);
        } catch (err) {
          console.error("GAPI init failed", err);
          reject(err);
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

export function setGapiAccessToken(accessToken) {
  if (!accessToken) return;
  if (!gapi.client) {
    console.warn("gapi.client is not ready yet, token will be set later");
    return;
  }
  gapi.client.setToken({ access_token: accessToken });
}

let _gapiInitPromise = null;
function ensureGapiInit() {
  if (!_gapiInitPromise) {
    _gapiInitPromise = initGapiClient().catch((err) => {
      console.error("Calendar gapi init failed:", err);
      _gapiInitPromise = null;
      throw err;
    });
  }
  return _gapiInitPromise;
}
function ensureCalendarReady() {
  if (!gapi.client?.calendar?.events) {
    throw new Error("Google Calendar API not initialized");
  }
}
export async function listEventsGapi(params = {}) {
  ensureCalendarReady();
  const resp = await gapi.client.calendar.events.list({
    calendarId: "primary",
    showDeleted: false,
    singleEvents: true,
    orderBy: "startTime",
    ...params,
  });
  return resp.result;
}
export async function insertEventGapi(resource) {
  ensureCalendarReady();
  const resp = await gapi.client.calendar.events.insert({
    calendarId: "primary",
    resource,
  });
  return resp.result;
}
export async function patchEventGapi(eventId, resource) {
  ensureCalendarReady();
  const resp = await gapi.client.calendar.events.patch({
    calendarId: "primary",
    eventId,
    resource,
  });
  return resp.result;
}
export async function deleteEventGapi(eventId) {
  ensureCalendarReady();
  await gapi.client.calendar.events.delete({
    calendarId: "primary",
    eventId,
  });
}
export async function createGoogleMeetEvent(title, startISO, endISO) {
  ensureCalendarReady();
  const resp = await gapi.client.calendar.events.insert({
    calendarId: "primary",
    conferenceDataVersion: 1,
    resource: {
      summary: title,
      start: { dateTime: startISO },
      end: { dateTime: endISO },
      conferenceData: {
        createRequest: {
          requestId: `meet-${Date.now()}`,
          conferenceSolutionKey: { type: "hangoutsMeet" },
        },
      },
    },
  });
  const meetLink = resp.result?.conferenceData?.entryPoints?.find(
    (ep) => ep.entryPointType === "video",
  )?.uri;
  return {
    meetLink: meetLink || null,
    eventId: resp.result?.id || null,
  };
}
export async function fetchGoogleUserInfo(accessToken) {
  try {
    const res = await fetch("https://www.googleapis.com/oauth2/v2/userinfo", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.ok) {
      const data = await res.json();
      localStorage.setItem("user_email", data.email);
      localStorage.setItem("user_name", data.name);
      return data;
    }
  } catch (error) {
    console.error("Failed to fetch Google user info:", error);
  }
  return null;
}

const STORAGE_KEYS = {
  token: "gauth_access_token",
  expiry: "gauth_token_expiry",
  email: "gauth_user_email",
};

function _readPersistedAuth() {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.token);
    const expiry = Number(localStorage.getItem(STORAGE_KEYS.expiry) || 0);
    const email = localStorage.getItem(STORAGE_KEYS.email);
    if (!token || !expiry) return null;
    if (Date.now() >= expiry) {
      _clearPersistedAuth();
      return null;
    }
    return { token, email };
  } catch {
    return null; 
  }
}

function _writePersistedAuth(token, email, expiresInSeconds) {
  try {
    const expiry = Date.now() + (expiresInSeconds || 3600) * 1000;
    localStorage.setItem(STORAGE_KEYS.token, token);
    localStorage.setItem(STORAGE_KEYS.expiry, String(expiry));
    if (email) localStorage.setItem(STORAGE_KEYS.email, email);
  } catch {
    // Ignore write failures — the session just won't survive a refresh.
  }
}

function _clearPersistedAuth() {
  try {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.expiry);
    localStorage.removeItem(STORAGE_KEYS.email);
  } catch {
    // Ignore.
  }
}

const _persisted = _readPersistedAuth();

let _accessToken = _persisted?.token || null;
let _userEmail = _persisted?.email || null;
let _authError = "";
const _listeners = new Set();

function _emit() {
  _listeners.forEach((fn) => fn());
}

async function _applyToken(token, email, expiresInSeconds) {
  _accessToken = token;
  _userEmail = email;
  _authError = "";
  _writePersistedAuth(token, email, expiresInSeconds);
  _emit(); // let Drive start working immediately — it doesn't need gapi

  try {
    await ensureGapiInit(); // wait for gapi.client to be ready
    setGapiAccessToken(token); // then hand the token to Calendar
  } catch {
    // Calendar init failing shouldn't block Drive features.
  }
}
function _applyError(msg) {
  _authError = msg;
  _emit();
}
function _clear() {
  _accessToken = null;
  _userEmail = null;
  _clearPersistedAuth();
  _emit();
}
function _subscribe(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}
let _rehydrated = false;
function _rehydrateGapiToken() {
  if (_rehydrated) return;
  _rehydrated = true;
  if (_accessToken) {
    ensureGapiInit()
      .then(() => setGapiAccessToken(_accessToken))
      .catch(() => {});
  }
}

export function useGoogleAuth() {
  const accessToken = useSyncExternalStore(_subscribe, () => _accessToken);
  const userEmail = useSyncExternalStore(_subscribe, () => _userEmail);
  const authError = useSyncExternalStore(_subscribe, () => _authError);
  useEffect(() => {
    ensureGapiInit();
    _rehydrateGapiToken();
  }, []);
  const login = useGoogleLogin({
    scope: SCOPES,
    onSuccess: async (tokenResponse) => {
      const data = await fetchGoogleUserInfo(tokenResponse.access_token);
      _applyToken(
        tokenResponse.access_token,
        data?.email || null,
        tokenResponse.expires_in,
      );
    },
    onError: () => _applyError("Could not connect to Google."),
  });
  const connect = useCallback(() => login(), [login]);
  const disconnect = useCallback(() => {
    googleLogout();
    _clear();
  }, []);

  return { accessToken, userEmail, authError, gisReady: true, connect, disconnect };
}
